import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { store } from 'components/Store';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import InputError from './InputError';
import useOrganizationInfoDescriptionFunctions from 'components/Dashboard/hooks/useOrganizationInfoDescriptionFunctions';
import time from 'utils/time';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import WebsiteSettingsTopics from 'components/Settings/WebsiteFeeds/WebsiteSettingsTopics';
import ModalComponent from 'components/Common/Modal';
import getAppSite from 'utils/getAppSite';

const ContentPopup = (props) => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { activePlan, team, keywordsLists: stateKeywordsLists } = state;
  const { code, teamName } = activePlan;

  const {
    setShowPopup,
    selectedTopic: selectedTopicProp,
    keywordsLists,
    setTriggerLoadSummary,
    settingsClient,
    onDescriptionSaved,
  } = props;

  const [selectedTopic, setSelectedTopic] = useState(selectedTopicProp);
  const [inputError, setInputError] = useState(null);
  const [action, setAction] = useState(null);
  const textAreaRef = useRef(null);

  const isParliamentarian = parliamentarianUsers(code);

  const [loadingUpdateDescription, setLoadingUpdateDescription] = useState(false);
  const [loadingInitialDescription, setLoadingInitialDescription] = useState(false);
  const [initialDescription, setInitialDescription] = useState(null);
  const [currentDescription, setCurrentDescription] = useState(null);

  const {
    removeAndUpdateOrganizationDescription,
    getClientDescription,
    removeOrganizationDescription,
    updateOrganizationInfoDescription,
  } = useOrganizationInfoDescriptionFunctions();

  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();

  const descriptionIsChanged =
    currentDescription !== initialDescription?.organisationDescription &&
    !(currentDescription === '' && initialDescription === null);

  const topicParam = parseFloat(getUrlParam('topic-id'));

  let alreadyHasDescription = initialDescription?.lastModified;

  const closeOnActionCondition = useMemo(
    () => settingsClient || (agencyUser && selectedTopic?.id === topicParam) || !agencyUser,
    [keywordsLists, selectedTopic?.id, topicParam, settingsClient]
  );

  const teamIdForRequest = settingsClient?.teamId;

  const handleClick = async (action) => {
    let toggleHasClientOrganisationDescription = (id, val) => {
      let keywordsListsToState = stateKeywordsLists.map((item) => {
        if (item?.id === id) {
          return { ...item, hasClientOrganisationDescription: val };
        } else {
          return item;
        }
      });
      dispatch({
        type: 'MODIFY_SECTION',
        parameter: 'keywordsLists',
        value: keywordsListsToState,
      });
      setSelectedTopic({
        ...selectedTopic,
        hasClientOrganisationDescription: val,
      });
    };
    let text = currentDescription;
    let removeAction = async () => {
      setLoadingUpdateDescription(true);
      const response = await removeOrganizationDescription({
        keywordListId: agencyUser ? selectedTopic?.id : undefined,
        teamId: teamIdForRequest,
      });
      setLoadingUpdateDescription(false);
      if (response) {
        createNotification('success', 'Description has been removed');
        if (agencyUser) {
          toggleHasClientOrganisationDescription(selectedTopic?.id, null);
        }
        if (settingsClient && onDescriptionSaved) {
          onDescriptionSaved(null);
        }
        setAction(null);
        if (closeOnActionCondition) {
          setTriggerLoadSummary?.(true);
          setShowPopup(false);
        }
      }
      return;
    };
    setAction(action);
    if (text?.length === 0) {
      removeAction();
      return;
    }
    setLoadingUpdateDescription(true);
    let response;
    if (alreadyHasDescription) {
      response = await removeAndUpdateOrganizationDescription({
        value: text,
        keywordListId: agencyUser ? selectedTopic?.id : undefined,
        teamId: teamIdForRequest,
      });
    } else {
      response = await updateOrganizationInfoDescription({
        value: text,
        keywordListId: agencyUser ? selectedTopic?.id : undefined,
        teamId: teamIdForRequest,
      });
    }

    setLoadingUpdateDescription(false);
    if (response) {
      createNotification(
        'success',
        `Description ${initialDescription?.authorFullName && initialDescription?.lastModified ? 'updated' : 'saved'}`
      );
      if (agencyUser) {
        toggleHasClientOrganisationDescription(selectedTopic?.id, true);
      }
      if (settingsClient && onDescriptionSaved) {
        onDescriptionSaved({
          authorFullName: response?.authorFullName,
          lastModified: response?.lastModified,
          organisationDescription: response?.organisationDescription ?? text,
        });
      }
      setAction(null);
      if (closeOnActionCondition) {
        setTriggerLoadSummary?.(true);
        setShowPopup(false);
      }
    }
  };

  let forCondition = useMemo(
    () =>
      (agencyUser && keywordsLists?.length) ||
      (settingsClient && settingsClient?.teamName?.length) ||
      (!agencyUser && !settingsClient && teamName?.length),
    [teamName, agencyUser, keywordsLists, settingsClient]
  );

  const getInitialDescription = useRef(null);
  getInitialDescription.current = async () => {
    const setTextAreaValue = (val) => {
      if (textAreaRef?.current) {
        setCurrentDescription(val);
        if (agencyUser) {
          setTimeout(() => {
            textAreaRef?.current.focus();
          }, 10);
        } else {
          textAreaRef?.current.focus();
        }
      }
    };
    if (settingsClient && newAgencyPlan) {
      const initialState =
        !settingsClient?.organisationDescription && !settingsClient?.organisationDescriptionLastModified
          ? null
          : {
              authorFullName: settingsClient?.organisationDescriptionAuthorFullName,
              lastModified: settingsClient?.organisationDescriptionLastModified,
              organisationDescription: settingsClient?.organisationDescription ?? '',
            };
      if (initialState) {
        setTextAreaValue(initialState.organisationDescription);
        setInitialDescription(initialState);
      } else {
        setTextAreaValue('');
        setInitialDescription(null);
      }
      return;
    }
    if (agencyUser) {
      setLoadingInitialDescription(true);
      const descriptionRes = await getClientDescription({
        keywordListId: selectedTopic?.id,
      });
      if (descriptionRes?.organisationDescription) {
        setTextAreaValue(descriptionRes?.organisationDescription);
        setInitialDescription({
          authorFullName: descriptionRes.authorFullName,
          lastModified: descriptionRes.lastModified,
          organisationDescription: descriptionRes?.organisationDescription,
        });
      } else {
        setTextAreaValue('');
        setInitialDescription(null);
      }
      setLoadingInitialDescription(false);
    } else {
      setTextAreaValue(team?.organisationDescription ?? '');
      setInitialDescription({
        authorFullName: team.organisationDescriptionAuthorFullName,
        lastModified: team.organisationDescriptionLastModified,
        organisationDescription: team?.organisationDescription,
      });
    }
  };

  useEffect(() => {
    getInitialDescription.current();
    setInputError(null);
  }, [selectedTopic, settingsClient?.teamId, settingsClient?.organisationDescriptionLastModified]);

  const currentDescriptionValueValidator = currentDescription?.trim()?.length > 100;
  const typingTimeOut = useRef(null);

  return (
    <div className={`tailor-summaries-modal ${agencyUser && keywordsLists.length >= 2 ? 'many-clients' : ''}`}>
      <p className='popup-header px-3 px-lg-5 flex-centered'>
        <span>
          Tailor your AI summaries and briefings {forCondition ? 'for' : ''}{' '}
          <span>
            {settingsClient?.teamName || (!agencyUser && teamName?.length ? teamName : '')}
          </span>
        </span>
        {agencyUser && !settingsClient && (
          <div className='tailor-summaries-dropdown-component'>
            <DropdownComponent
              mainItem={() => <span className='hidden-lines hidden-one-line'>{selectedTopic?.name}</span>}
              hideArrowDisabled={keywordsLists.length === 1}
            >
              <WebsiteSettingsTopics
                selectedTopic={selectedTopic}
                setSelectedTopic={setSelectedTopic}
                agencyUser
                topicsVariant
                hideButtons
                keywordsListsToUseProp={keywordsLists}
                hideAddTopic
              />
            </DropdownComponent>
          </div>
        )}
      </p>

      <div className='separator' />
      <div className='popup-body p-3 px-lg-5 pb-lg-2'>
        <div>
          <p>
            Enter as much information as possible about {isParliamentarian ? '' : 'your'}{' '}
            {agencyUser || newAgencyPlan
              ? `${team?.expires ? 'prospective ' : ''}client and their work`
              : `${
                  isParliamentarian ? '' : `${getAppSite() === 'usa' ? 'organization' : 'organisation'} and your`
                } work`}
            . This text will be saved in order to improve the relevance of the AI-generated briefings and summaries of
            updates.
          </p>
          <div className='position-relative'>
            <textarea
              ref={textAreaRef}
              value={currentDescription}
              rows={4}
              className='input-empty w-100 border border-grey rounded-sm p-3'
              style={{ resize: 'none' }}
              onChange={(e) => {
                setCurrentDescription(e.target.value);
                setInputError(null);
              }}
              onKeyDown={(e) => {
                if (typingTimeOut.current) clearTimeout(typingTimeOut.current);
                typingTimeOut.current = setTimeout(() => {
                  if (e.target?.value?.trim()?.length < 100) {
                    createNotification('danger', 'Please enter a description that is at least 100 characters');
                  }
                }, 500);
              }}
              disabled={loadingInitialDescription}
            ></textarea>
          </div>
          <div
            className={`tailor-summary-action-container flex-centered justify-content-${
              inputError || initialDescription?.lastModified ? 'between' : 'end'
            }  mt-2`}
          >
            {inputError ? (
              <InputError errText={inputError} />
            ) : (
              <>
                {initialDescription?.lastModified && (
                  <p className='mb-0 pt-3 last-edited-by-subtext'>
                    <i>
                      Last edited by {initialDescription?.authorFullName || 'PolicyMogul'} on{' '}
                      {time(initialDescription?.lastModified).formatLongDate(true)}
                    </i>
                  </p>
                )}
              </>
            )}

            <div className='flex-centered pt-1'>
              <button
                className={`action-button general-button px-3 filter-action-button update-button ${
                  agencyUser ? 'wide' : ''
                }`}
                onClick={() => {
                  handleClick('show');
                }}
                disabled={
                  !currentDescriptionValueValidator ||
                  loadingUpdateDescription ||
                  loadingInitialDescription ||
                  !descriptionIsChanged
                }
              >
                {loadingUpdateDescription && action === 'show' && <i className='fas fa-spinner fa-spin mx-2' />}
                <span>
                  {initialDescription?.lastModified ? 'Update' : 'Save'} description
                  {agencyUser ? ` for ${selectedTopic?.name}` : ''}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div className='mb-2 flex-centered justify-content-end'>
          <button className='general-button delete-button px-4 mr-3 py-1' onClick={() => setShowPopup(false)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const TailorSummaryModal = (props) => {
  return (
    <ModalComponent
      maxWidth={850}
      isOpen={props.showPopup}
      setIsOpen={props.setShowPopup}
      overlayClassName={'react-modal-overlay modal-close-position-adjustment'}
    >
      <ContentPopup {...props} />
    </ModalComponent>
  );
};

export default TailorSummaryModal;
